import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Link,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Badge from '@/components/common/Badge';
import type { ReferenceItem } from '@/types';

interface ReferencesModalProps {
  open: boolean;
  onClose: () => void;
  references: ReferenceItem[];
  ticker: string;
}

function timestampToSeconds(ts?: string): number {
  if (!ts) return 0;
  const parts = ts.split(':').map(Number);
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
}

export default function ReferencesModal({ open, onClose, references, ticker }: ReferencesModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { bgcolor: 'background.paper' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {ticker} - 视频引用
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {references.length === 0 ? (
            <Typography color="text.secondary">无引用</Typography>
          ) : (
            references.map((ref, index) => {
              const seconds = timestampToSeconds(ref.timestamp);
              const youtubeUrl = `https://www.youtube.com/watch?v=${ref.video_id}${seconds > 0 ? `&t=${seconds}` : ''}`;

              return (
                <Box key={`${ref.video_id}-${index}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {ref.timestamp && (
                      <Badge variant="blue" size="small">
                        {ref.timestamp}
                      </Badge>
                    )}
                    {ref.category && (
                      <Badge
                        variant="gray"
                        size="small"
                      >
                        {ref.category}
                      </Badge>
                    )}
                    {ref.action && (
                      <Badge
                        variant={ref.action.includes('买') || ref.action.includes('加') ? 'green' : ref.action.includes('卖') || ref.action.includes('减') ? 'red' : 'amber'}
                        size="small"
                      >
                        {ref.action}
                      </Badge>
                    )}
                  </Box>

                  {ref.viewpoint && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {ref.viewpoint}
                    </Typography>
                  )}

                  <Link
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.875rem',
                    }}
                  >
                    打开视频
                    <OpenInNewIcon fontSize="small" />
                  </Link>

                  {index < references.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              );
            })
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
